import { Injectable } from "@angular/core";
import { v4 as uuid } from "uuid";
import * as FormatUtils from "@vision/core-map-api/src/utils/FormatUtils";
import { Viewerlayer } from "../../model/viewerlayer";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { ViewerConfigurationService } from "../viewer-configuration.service";
import { LayerSource } from "../../model/layersource";
import { ICustomOverlayProperties } from "../../interfaces/i-custom-overlay-properties";

@Injectable({
  providedIn: "root",
})

/**
 * Interact with layer model object
 * Changes on map by layer
 * Add layers to map
 * Create layers
 */
/* eslint-disable class-methods-use-this */
export class LayerService {
  constructor(private viewerConfigurationService: ViewerConfigurationService) {}

  getBaseLayers(layers: Viewerlayer[]): Viewerlayer[] {
    return layers.filter((layer) => layer.base).sort((l1, l2) => l1.order - l2.order);
  }

  getOverlayLayers(layers: Viewerlayer[]): Viewerlayer[] {
    return layers.filter((layer) => !layer.base).sort((l1, l2) => l2.order - l1.order);
  }

  getCollectionLayers(layers: Viewerlayer[]): Viewerlayer[] {
    return layers
      .filter((layer) => layer.layerType === ViewerConstants.LAYER_TYPE.COLLECTIONLAYER)
      .sort((l1, l2) => l2.order - l1.order);
  }

  /**
   * Add layers to map map
   * Detect layer type and add specifical layer
   * @param {Viewerlayer[]} layers
   * @param map
   */
  addLayersToMap(layers: Viewerlayer[], map: any) {
    layers.forEach((layer) => {
      try {
        this.addLayerToMap(layer, map);
      } catch (error) {
        // A malformed style (or any other layer-construction failure) must not abort the rest of
        // the batch — one bad layer shouldn't take down every other layer being imported alongside it.
        console.error("LayerService: failed to add layer to map, skipping it", layer.id, layer.name, error);
      }
    });
  }

  addLayerToMap(viewerLayer: Viewerlayer, map: any) {
    this.addLayerFromDef(viewerLayer, map);
  }

  addLayerFromDef(viewerLayer: Viewerlayer, map: any): string {
    if (viewerLayer.base) {
      return map.addBaseLayerFromDef(viewerLayer);
    }
    const result = map.addOverlayLayerFromDef(viewerLayer);
    return result;
  }

  createFeatureWkt(map: any, wkt: string, epsg: string, data: any) {
    const wktGeographic = FormatUtils.transformWkt(wkt, {
      dataProjection: epsg,
      featureProjection: ViewerConstants.PROJECTIONS.WGS84,
    });
    return map.createVectorFeature(uuid(), wktGeographic, data);
  }

  createLayerFromLocalCollection(collection: any): Viewerlayer {
    // Defense-in-depth (evidence-layer defect): a null-geometry feature crashes
    // LayerFactory at map-add time (getGeometry().clone()) and the WHOLE layer is then
    // skipped. Drop those features here so every caller is covered, including
    // collections reloaded from IndexedDB that bypass prepareGeoJson.
    if (Array.isArray(collection?.features)) {
      const usable = collection.features.filter((f: any) => f && f.geometry);
      if (usable.length !== collection.features.length) {
        console.warn(
          `createLayerFromLocalCollection: dropped ${collection.features.length - usable.length} null-geometry feature(s)`,
        );
        collection = { ...collection, features: usable };
      }
    }
    const source = new LayerSource();
    source.name = ViewerConstants.SOURCE_NAME.VECTOR;
    source.epsg = collection.properties.epsg;
    source.features = collection;
    const { userId } = this.viewerConfigurationService.getViewerConfiguration();

    return {
      id: collection.properties.id,
      userId,
      name: collection.properties.name,
      base: false,
      baseLayer: false,
      visible: true,
      owner: true,
      removable: true,
      editable: true,
      sharable: false,
      enabled: true,
      layerType: ViewerConstants.SOURCE_NAME.VECTOR,
      sourceName: ViewerConstants.SOURCE_NAME.VECTOR,
      source,
      bbox: collection.properties.bbox,
      order: ViewerConstants.USER_LAYER_BASE_ZINDEX,
      overlayType: collection.properties.fileType,
      customOverlayProperties: collection.properties.customOverlayProperties,
    } as Viewerlayer;
  }

  /**
   * Builds a Viewerlayer definition for a GeoTIFF, persisting its raw bytes (not just a URL) so the
   * layer can be re-added to the map after a page reload. Georeferencing (extent, projection) is
   * read directly from the file by the map's GeoTIFF layer.
   * @param {Blob} blob The (possibly tie-point-corrected) GeoTIFF file, as prepared by GeoTiffRendererService.prepareForMap.
   * @param {string} [name] Display name for the layer.
   * @param {{min: number, max: number, nodata?: number}} [stats] Band value stretch, as computed by
   *   GeoTiffRendererService.prepareForMap — without it the WebGL layer normalizes against the
   *   full data-type range and renders solid black.
   */
  createGeoTiffLayerDef(blob: Blob, name?: string, stats?: { min: number; max: number; nodata?: number }): Viewerlayer {
    const source = new LayerSource();
    source.name = ViewerConstants.SOURCE_NAME.GEOTIFF;
    source.blob = blob;
    source.min = stats?.min;
    source.max = stats?.max;
    source.nodata = stats?.nodata;
    const { userId } = this.viewerConfigurationService.getViewerConfiguration();

    return {
      id: uuid(),
      userId,
      name: name ?? "GeoTIFF Layer",
      base: false,
      baseLayer: false,
      visible: true,
      owner: true,
      removable: true,
      editable: true,
      sharable: false,
      enabled: true,
      layerType: ViewerConstants.SOURCE_NAME.GEOTIFF,
      sourceName: ViewerConstants.SOURCE_NAME.GEOTIFF,
      source,
      order: ViewerConstants.USER_LAYER_BASE_ZINDEX,
    } as Viewerlayer;
  }

  /**
   * Resolves a GeoTIFF layer's own georeferenced extent (read from the file by the map's
   * GeoTIFF layer) and returns it as a Web Mercator WKT polygon, for use as `Viewerlayer.bbox`
   * (powers the "highlight on map" / fit-to-layer action, same as GeoJSON layers).
   * @returns The WKT bbox, or null if the layer isn't a GeoTIFF layer or its extent couldn't be resolved.
   */
  async resolveGeoTiffBbox(layerId: string, map: any): Promise<string | null> {
    const olLayer = map.getLayerById(layerId);
    if (!olLayer || typeof olLayer.getGeoTiffView !== "function") return null;

    try {
      const view = await olLayer.getGeoTiffView();
      const mercatorExtent = FormatUtils.transformExtent(
        view.extent,
        view.projection,
        ViewerConstants.PROJECTIONS.WebMercator,
      );
      return FormatUtils.geometryToWKT(FormatUtils.extentToGeometry(mercatorExtent));
    } catch (err) {
      console.error("LayerService: failed to resolve GeoTIFF extent", err);
      return null;
    }
  }

  removeLayerById(layerId: string, map: any): void {
    const mapLayer = map.getLayerById(layerId);
    map.removeLayer(mapLayer);
  }

  /**
   * Update properties on layers based on other list layers
   * @param {Viewerlayer[]} parentLayers
   * @param {Viewerlayer[]} newLayers returned layers with updated properties
   * @returns {Viewerlayer[]}
   */
  syncronizeLayers(parentLayers: Viewerlayer[], newLayers: Viewerlayer[]): Viewerlayer[] {
    newLayers.forEach((layer) => {
      const parent = parentLayers.find((item) => item.id === layer.id);
      if (parent) {
        layer.enabled = parent.enabled;
        layer.visible = parent.visible;
        layer.order = parent.order;
        layer.opacity = parent.opacity;
      }
    });
    return newLayers;
  }

  getCustomOverlayProperties(layer: Viewerlayer, features: any): ICustomOverlayProperties {
    let customOverlayProperties: ICustomOverlayProperties;

    if (layer && features.length > 0) {
      const layerProperties = Object.keys(features[0].values_);
      const primaryIndex = layerProperties.findIndex((item) => item.toLowerCase() === "name");
      const secondaryIndex = primaryIndex === 1 ? 2 : 1;

      // eslint-disable-next-line prefer-const
      customOverlayProperties = {
        iconSrc: "assets/images/csv_pin_btn.svg",
        primaryField: primaryIndex < 0 ? layerProperties[1] : layerProperties[primaryIndex],
        secondaryField: layerProperties[secondaryIndex],
        layerProperties,
      };
    }
    return customOverlayProperties;
  }
}
